import { cn } from "@/infrastructure/lib/utils";
import { ScrollArea } from "./scroll-area";
import { forwardRef, HTMLAttributes, ReactNode, useImperativeHandle, useRef, useState, useEffect } from "react";
import { Maximize2, Minimize2 } from "lucide-react";

// Omit 'title' do HTMLAttributes para podermos usar nosso próprio tipo
export interface LogTerminalProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  children?: ReactNode;
  title?: ReactNode;
  actionButtons?: ReactNode; // Botões de ação que aparecerão à direita, antes do botão de expandir
}

export interface LogTerminalRef {
  scrollToBottom: () => void;
  // Rola para o fim IGNORANDO o estado de auto-scroll: para navegação explícita
  // (ex.: abrir uma sessão), onde o usuário espera ver o conteúdo mais recente
  // mesmo tendo rolado para cima antes.
  forceScrollToBottom: () => void;
  isScrolledToBottom: () => boolean;
  shouldAutoScroll: boolean;
}

const LogTerminal = forwardRef<LogTerminalRef, LogTerminalProps>(
  ({ className, children, title = "Terminal", actionButtons, ...props }, ref) => {
    const scrollAreaRef = useRef<HTMLDivElement>(null);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
    
    // Desativar o scroll do body quando o terminal estiver em tela cheia
    useEffect(() => {
      if (isFullscreen) {
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "";
      }
      
      return () => {
        document.body.style.overflow = "";
      };
    }, [isFullscreen]);
    
    // Verificar se o usuário está no final do scroll
    const isScrolledToBottom = () => {
      const viewport = scrollAreaRef.current?.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement | null;
      if (!viewport) return true;
      
      // Calcular se está próximo ao final (margem de 30px)
      const isAtBottom = Math.abs((viewport.scrollHeight - viewport.clientHeight) - viewport.scrollTop) < 30;
      return isAtBottom;
    };
    
    // Monitorar eventos de scroll para atualizar o estado de auto-scroll
    useEffect(() => {
      const viewport = scrollAreaRef.current?.querySelector('[data-radix-scroll-area-viewport]');
      if (!viewport) return;
      
      const handleScroll = () => {
        // Se o usuário rolar para o final, reativar o auto-scroll
        if (isScrolledToBottom()) {
          setShouldAutoScroll(true);
        } else {
          // Se o usuário rolar para cima, desativar o auto-scroll
          setShouldAutoScroll(false);
        }
      };
      
      viewport.addEventListener('scroll', handleScroll);
      return () => viewport.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollViewportsToBottom = () => {
      // Encontra o viewport interno do ScrollArea que realmente controla o scroll
      const viewport = scrollAreaRef.current?.querySelector('[data-radix-scroll-area-viewport]');
      if (viewport) {
        (viewport as HTMLElement).scrollTop = (viewport as HTMLElement).scrollHeight;
      }

      // Se estiver em modo de tela cheia, também encontra o ScrollArea no modo fullscreen
      if (isFullscreen) {
        const fullscreenViewport = document.querySelector('.fullscreen-terminal [data-radix-scroll-area-viewport]');
        if (fullscreenViewport) {
          (fullscreenViewport as HTMLElement).scrollTop = (fullscreenViewport as HTMLElement).scrollHeight;
        }
      }
    };

    // Expõe os métodos para o componente pai
    useImperativeHandle(ref, () => ({
      scrollToBottom: () => {
        // Só fazer scroll automático se shouldAutoScroll for true
        if (!shouldAutoScroll) return;
        scrollViewportsToBottom();
      },
      forceScrollToBottom: () => {
        // Navegação explícita: rola e reabilita o auto-scroll (o usuário voltou
        // ao fim do conteúdo, então novas linhas devem acompanhar).
        setShouldAutoScroll(true);
        scrollViewportsToBottom();
      },
      isScrolledToBottom,
      shouldAutoScroll
    }));

    const toggleFullscreen = () => {
      setIsFullscreen(!isFullscreen);
    };

    const terminalContent = (
      <>
        <div className="flex items-center justify-between px-4 py-2 bg-muted border-b border-hairline">
          <div className="flex items-center">
            <div className="flex space-x-2">
              <div className="w-3 h-3 bg-error rounded-full"></div>
              <div className="w-3 h-3 bg-warning rounded-full"></div>
              <div className="w-3 h-3 bg-success rounded-full"></div>
            </div>
            <div className="ml-4 text-xs font-medium text-foreground">{title}</div>
          </div>
          <div className="flex items-center">
            {actionButtons}
            <button
              onClick={toggleFullscreen}
              className="text-muted-foreground hover:text-foreground focus:outline-none ml-4"
              aria-label={isFullscreen ? "Sair da tela cheia" : "Expandir para tela cheia"}
            >
              {isFullscreen ? (
                <Minimize2 className="h-4 w-4" />
              ) : (
                <Maximize2 className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>
        <ScrollArea
          className="h-full p-4"
          ref={scrollAreaRef}
        >
          {children}
        </ScrollArea>
      </>
    );

    // Modal em tela cheia usando position: fixed diretamente, sem Dialog
    if (isFullscreen) {
      return (
        <div className="fixed inset-0 z-50 flex flex-col bg-surface-sunken text-foreground font-mono text-sm fullscreen-terminal">
          {terminalContent}
        </div>
      );
    }

    // Terminal normal quando não está em tela cheia
    return (
      <div
        className={cn(
          "relative rounded-md border border-hairline bg-surface-sunken text-foreground font-mono text-sm overflow-hidden",
          className
        )}
        {...props}
      >
        {terminalContent}
      </div>
    );
  }
);

LogTerminal.displayName = "LogTerminal";

export { LogTerminal };
